import React from 'react';
import PropTypes from 'prop-types';

import { Redirect } from 'react-router';

import { 
  Chip,
  IconButton,
  Tooltip,
  Typography,
}                                   from '@material-ui/core';

import { 
  ExitToApp as ExternalIcon,
}                                   from '@material-ui/icons';

import { FieldValue }             from "../../../packages/detail-panel";

import { useExecutionInputs } from '../hooks';
import { ExecutionInputDataTable } from '.';

import { 
  fmtBytes,
  fmtElapsedAgoFromIsoString,
  fmtIso8601AsLocalDateHrsMin,
} from '../../utils/formatters';

import { fmtExecutionResult } from '../formatters';

function fmtInputName(value) {
  if(!value) {
    return(null);
  }
  
  return(".../ "+value.split('/').slice(5).map(s => s.length>10?s.slice(0,7)+"...":s).join(' / '));
}

function fmtAttempts(value) {
  if(!value) {
    return(null);
  }
  return(
    <Typography variant='inherit'>{value.length}</Typography>
  );
}

function getSelectedColumnDefs(columnDefs, columnList) {
  const effectiveList=columnList?columnList:columnDefs.map((def) => def.name);
  return effectiveList.map((name) => columnDefs.find(def => def.name === name));
}

export default function ExecutionInputs(props) {
  
  const { execution, columns, propertyDefs, withResult, title } = props;  

  const [selected, setSelected] = React.useState(null);
  const [loadMoreInputs, setLoadMoreInputs] = React.useState(null);

  const { inputs, loading, error, moreInputs } = useExecutionInputs({
    executionId: execution, 
    withResult: withResult,
    orderBy: 'UpdateTime', order: 'DESC',
    subscribed: 'all',
    loadMoreInputs: loadMoreInputs,
  });
  
  const handleLoadMoreInputs = () => {
    if(moreInputs) {
      setLoadMoreInputs(moreInputs);
    }
  };
  
  const handleSelect = (executionInput) => {
    setSelected(executionInput);
  };

  const handleSelectByInputUri = (inputUri) => {
    setSelected(inputs.find( ei => ei.InputUri===inputUri));
  };

  if (error) {
    console.log("ERROR: ",error);
    return(<div>Error: see console</div>);
  }

  const executionInputPropertyDefs = [
    { name: 'SubmitTime', label: 'Submitted', options: { display: false, filter: false, customBodyRender: fmtIso8601AsLocalDateHrsMin } },
    { name: 'UpdateTime', label: 'Updated', options: { filter: false, customBodyRender: fmtIso8601AsLocalDateHrsMin } },
    { name: 'EndTime', label: 'Completed', options: { display: false, filter: false, customBodyRender: fmtElapsedAgoFromIsoString } },
    { name: 'InputSizeInBytes', label: 'Size', options: { filter: false, customBodyRender: fmtBytes }},
    { name: 'InputUri', label: 'Name', clickable: true, options: { 
      filter: false,
      customBodyRenderLite: (dataIndex) => <FieldValue 
        value={inputs[dataIndex].InputUri}
        formatter={fmtInputName}
        maxLength={80}
        onClick={handleSelectByInputUri}/> 
    } },
    { name: 'Attempts', label: 'Attempts', options: { display: false, filter:false, sort: false, customBodyRender: fmtAttempts }},
    { name: 'Status', options: { display: false, } },
    { name: 'Result', options: { customBodyRender: fmtExecutionResult } },
    { name: 'ResultSummary', label: 'Summary' },
  ];
  
  const effectivePropertyDefs = executionInputPropertyDefs.concat(propertyDefs);
  const selectedColumnDefs = getSelectedColumnDefs(effectivePropertyDefs, columns);

  if(selected) {
    return <Redirect push to={"/executions/"+selected.ExecutionId+"/"+window.btoa(selected.InputUri)} />;  
  }
  
  return (
    <React.Fragment>
      <ExecutionInputDataTable
        title={withResult?<Chip label={withResult} variant="outlined"/>:title}
        data={inputs}
        columns={selectedColumnDefs}
        onSelect={handleSelect}
        loading={loading}
        more={moreInputs?true:false}
        onLoadMore={handleLoadMoreInputs}
      /> 
    </React.Fragment>
  );
  
}

ExecutionInputs.propTypes = {
  execution: PropTypes.string,
  columns: PropTypes.array.isRequired,
  propertyDefs: PropTypes.array.isRequired,
  withResult: PropTypes.oneOf(['REJECTED','SKIPPED','FAILED','SUCCEEDED']),
  title: PropTypes.string,
};

ExecutionInputs.defaultProps = {
  columns: [
    'SubmitTime',
    'UpdateTime', 
    'EndTime', 
    'InputUri',
    'InputSizeInBytes', 
    'Attempts',
    'Status', 
    'Result',
    'ResultSummary',
  ],
  propertyDefs: [],
  title: "Execution Inputs",
};
